import useSessionEvents from '@/composables/v2/metrics/useSessionEvents';
import { onMounted, onUnmounted, ref, Ref, watch } from 'vue';
import { SessionEvent } from '@/models/Metrics/SessionEvent';

const isDebug = process.env.VUE_APP_DEBUG === 'true';

/**
 * Represents the identifier of the session for which events are being tracked.
 */
const sessionId = ref<string | undefined>(undefined);

export function setObservedSessionId(id: string | undefined): void {
    sessionId.value = id;
}

const METRICS_FLUSHING_INTERVAL = process.env.VUE_APP_METRICS_FLUSHING_INTERVAL ?? 5000; // In milliseconds
const METRICS_MAX_BUFFER_SIZE = process.env.VUE_APP_METRICS_MAX_BUFFER_SIZE ?? 10; // Session events are flushed when the buffer size reaches this value

/**
 * A reactive reference that holds the identifier for the interval responsible
 * for flushing session events. The value can either be a number representing
 * the interval ID (used for clearing the interval) or null if no interval is set.
 */
const globalMetricsFlushingIntervalId: Ref<number | null> = ref(null);

/**
 * Manages session metrics including event restoration, buffering, and periodic flushing of session events.
 *
 * @return {Object} An object containing methods to dispatch session events.
 * @return {Function} return.dispatchSessionEvents - A method to dispatch session events. Allows adding events to the buffer and triggers flushing based on conditions.
 * @param newObservedSessionId
 */
export default function useMetrics(newObservedSessionId: string | undefined = undefined) {
    const { restoreEventsFromLocalStorage, storeSessionEventInBuffer, flushSessionEvents, eventsBuffer } =
        useSessionEvents();

    if (newObservedSessionId) {
        setObservedSessionId(newObservedSessionId);
    }

    /**
     * In the case of many useMetrics instanciations, this local variable allows knowing
     * which useMetrics composable is responsible to clear the interval.
     */
    let localMetricsFlushingIntervalId: number | null = null;

    const enableSessionMetrics = (id: string | undefined = undefined): void => {
        const currentSessionId = id ?? sessionId.value;

        if (!currentSessionId) {
            return;
        }

        restoreEventsFromLocalStorage(currentSessionId);

        if (!globalMetricsFlushingIntervalId.value) {
            localMetricsFlushingIntervalId = setInterval(async () => {
                await flushSessionEvents(currentSessionId);
            }, METRICS_FLUSHING_INTERVAL);

            globalMetricsFlushingIntervalId.value = localMetricsFlushingIntervalId;
        }
    };

    const disableSessionMetrics = (): void => {
        if (localMetricsFlushingIntervalId) {
            clearInterval(localMetricsFlushingIntervalId);
        }
    };

    watch(sessionId, (newSessionId, oldSessionId) => {
        if (oldSessionId && newSessionId !== oldSessionId) {
            disableSessionMetrics();
            enableSessionMetrics(newSessionId);
        }
    });

    const flushSessionEventsIfCurrentSessionExist = async () => {
        const currentSessionId = sessionId.value;
        if (!currentSessionId) {
            return;
        }
        await flushSessionEvents(currentSessionId);
    };

    onMounted(() => {
        disableSessionMetrics();
        enableSessionMetrics();
    });

    onUnmounted(() => {
        disableSessionMetrics();
    });

    const dispatchSessionEvents = (events: SessionEvent[], now = false): void => {
        if (isDebug) {
            console.log('Dispatching event:');
            console.table(events[0]);
        }

        const currentSessionId = sessionId.value;

        if (!currentSessionId) {
            console.warn('A Session ID must be provided to send session events.');
            return;
        }

        events.forEach((event) => {
            storeSessionEventInBuffer(currentSessionId, event);
        });

        const eventsBufferSize = (eventsBuffer[currentSessionId] ?? []).length;

        if (now || eventsBufferSize >= METRICS_MAX_BUFFER_SIZE) {
            flushSessionEvents(currentSessionId);
        }
    };

    const forceFlushSessionEvents = (): void => {
        dispatchSessionEvents([], true);
    };

    return {
        dispatchSessionEvents,
        forceFlushSessionEvents,
        flushSessionEventsIfCurrentSessionExist,
    };
}
